import { SpeechSegment } from '../../../services/api.service';

import { canonicalizeSpeakerDisplayName } from '../../../utils/speaker-display-names';

export const SPEAKER_COLORS = ['#1B80FC', '#00B894', '#FF428A', '#F59E0B', '#6C5CE7', '#E17055'];

export const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 2, 0.75] as const;

export function parseTimeToSeconds(time: string): number {
  if (!time) return 0;
  const parts = time.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

export function formatSecondsShort(sec: number): string {
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function getSegmentStartSec(seg: SpeechSegment): number {
  if (typeof seg.start_seconds === 'number' && Number.isFinite(seg.start_seconds)) {
    return seg.start_seconds;
  }
  return parseTimeToSeconds(seg.start_time);
}

export function getSegmentEndSec(seg: SpeechSegment): number {
  if (typeof seg.end_seconds === 'number' && Number.isFinite(seg.end_seconds)) {
    return seg.end_seconds;
  }
  const parsed = parseTimeToSeconds(seg.end_time);
  if (parsed > 0) return parsed;
  const start = getSegmentStartSec(seg);
  const dur = seg.duration_seconds;
  if (typeof dur === 'number' && dur > 0) return start + dur;
  return start;
}

type SegmentTimeRange = {
  startSec: number;
  endSec?: number;
};

function segmentEffectiveEnd(
  seg: SegmentTimeRange,
  nextStartSec?: number,
  fallbackSpanSec = 8,
): number {
  const start = seg.startSec;
  const end = seg.endSec;
  if (typeof end === 'number' && end > start) return end;
  if (typeof nextStartSec === 'number' && nextStartSec > start) return nextStartSec;
  return start + fallbackSpanSec;
}

/** Индекс сегмента, в интервале которого находится timeSec. */
export function findActiveSegmentIndex(
  segments: SegmentTimeRange[],
  timeSec: number,
): number {
  if (!segments.length || !Number.isFinite(timeSec)) return -1;

  for (let i = 0; i < segments.length; i++) {
    const start = segments[i].startSec;
    const end = segmentEffectiveEnd(segments[i], segments[i + 1]?.startSec);
    if (timeSec >= start - 0.05 && timeSec < end) return i;
  }

  const last = segments[segments.length - 1];
  if (timeSec >= last.startSec) return segments.length - 1;
  return -1;
}

export function formatSpeakerTrackName(name: string): string {
  return name.replace(/^SPEAKER_(\d+)$/i, (_, num) => `SP_${num}`);
}

/** Убирает «(ТТК)» и прочие организационные суффиксы. */
export function stripSpeakerOrgSuffix(name: string): string {
  return name.replace(/\s*\([^)]*\)\s*$/g, '').trim();
}

/** Краткое имя для узкой колонки под плеером: «Галихан Мухамед-Рахимов» → «ГМ-Р». */
export function formatSpeakerShortName(name: string): string {
  const cleaned = canonicalizeSpeakerDisplayName(stripSpeakerOrgSuffix(name));
  if (!cleaned) return '—';
  if (/^SPEAKER_\d+$/i.test(cleaned)) {
    return formatSpeakerTrackName(cleaned);
  }

  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].length <= 10 ? parts[0] : `${parts[0].slice(0, 8)}…`;
  }

  const firstInitial = parts[0][0]?.toLocaleUpperCase('ru-RU') ?? '';
  const last = parts[parts.length - 1];
  const hyphenChunks = last.split('-').filter(Boolean);
  if (hyphenChunks.length >= 2) {
    const lastAbbr = hyphenChunks
      .map(chunk => chunk[0]?.toLocaleUpperCase('ru-RU') ?? '')
      .join('-');
    return `${firstInitial}${lastAbbr}`;
  }

  const lastInitial = last[0]?.toLocaleUpperCase('ru-RU') ?? '';
  return `${firstInitial}${lastInitial}`;
}

/** Полное имя без «(ТТК)» — для блоков с достаточной шириной (Распределение). */
export function formatSpeakerFullName(name: string): string {
  const cleaned = canonicalizeSpeakerDisplayName(stripSpeakerOrgSuffix(name));
  if (!cleaned) return '—';
  if (/^SPEAKER_\d+$/i.test(cleaned)) {
    return formatSpeakerTrackName(cleaned);
  }
  return cleaned;
}

export function getSpeakerName(seg: SpeechSegment): string {
  const raw = seg.speaker_display || seg.speaker_name || seg.speaker_label || 'Участник';
  return canonicalizeSpeakerDisplayName(raw);
}

/** Сравнение имён спикеров (SPEAKER_09, SPEAKER_09 (RU), Baurzhan). */
export function speakerNamesMatch(a: string, b: string): boolean {
  const norm = (s: string) => s.replace(/\s*\([^)]*\)\s*$/g, '').trim().toLowerCase();
  const na = norm(a);
  const nb = norm(b);
  return na === nb || na.includes(nb) || nb.includes(na);
}

/** Объединение интервалов речи (без двойного подсчёта перекрытий). */
export function unionSpeechDurationSec(
  segments: SpeechSegment[],
  getStart: (s: SpeechSegment) => number = getSegmentStartSec,
  getEnd: (s: SpeechSegment) => number = getSegmentEndSec,
): number {
  const intervals = segments
    .map(seg => ({ start: getStart(seg), end: getEnd(seg) }))
    .filter(iv => iv.end > iv.start)
    .sort((a, b) => a.start - b.start);

  let total = 0;
  let cur: { start: number; end: number } | null = null;
  for (const iv of intervals) {
    if (!cur) {
      cur = { ...iv };
      continue;
    }
    if (iv.start <= cur.end) {
      cur.end = Math.max(cur.end, iv.end);
    } else {
      total += cur.end - cur.start;
      cur = { ...iv };
    }
  }
  if (cur) total += cur.end - cur.start;
  return total;
}

/** Сумма длительностей сегментов (может быть меньше union при коротких end_time). */
export function sumSegmentDurationsSec(segments: SpeechSegment[]): number {
  return segments.reduce(
    (sum, seg) => sum + Math.max(0, getSegmentEndSec(seg) - getSegmentStartSec(seg)),
    0,
  );
}
